import React from "react";

import { Alert, Button, message, Typography, Upload } from "antd";
import fileDownload from "js-file-download";
import PropTypes from "prop-types";

import { DownloadOutlined, InboxOutlined } from "@ant-design/icons";

import { useAppTheme } from "../../../context/ThemeProvider";
import { extractHostFromUrl } from "../../../Utils/utilityFunctions";

const { Text, Title } = Typography;

const MAX_ENTRIES = 500;

const UploadUsersCSV = ({ onUpload, onError, disabled }) => {
  const { COLORS } = useAppTheme();

  const parseCsvData = (csvText) => {
    const lines = csvText.split("\n").filter((line) => line.trim());

    if (lines.length < 2) {
      throw new Error(
        "CSV file must contain at least a header row and one data row"
      );
    }

    const headers = lines[0].split(",").map((h) => h.trim().toLowerCase());

    const requiredHeaders = ["email", "host"];
    const missingHeaders = requiredHeaders.filter((h) => !headers.includes(h));

    if (missingHeaders.length > 0) {
      throw new Error(`Missing required columns: ${missingHeaders.join(", ")}`);
    }

    const entries = [];

    for (let i = 1; i < lines.length; i += 1) {
      const values = [];
      let currentValue = "";
      let insideQuotes = false;

      for (let j = 0; j < lines[i].length; j += 1) {
        const char = lines[i][j];
        if (char === '"') {
          insideQuotes = !insideQuotes;
        } else if (char === "," && !insideQuotes) {
          values.push(currentValue.trim());
          currentValue = "";
        } else {
          currentValue += char;
        }
      }
      values.push(currentValue.trim());

      const row = {};
      headers.forEach((header, index) => {
        const value = values[index] || "";
        const cleaned = value.replace(/^"|"$/g, "").trim();
        if (cleaned !== "") {
          row[header] = cleaned;
        }
      });

      if (row.email || row.host) {
        const extractedHost = row.host
          ? extractHostFromUrl(row.host)
          : row.host;
        entries.push({
          email: (row.email || "").toLowerCase().trim(),
          host: (extractedHost || "").toLowerCase().trim(),
        });
      }
    }

    if (entries.length === 0) {
      throw new Error("No entries found in CSV");
    }

    if (entries.length > MAX_ENTRIES) {
      throw new Error(`Maximum ${MAX_ENTRIES} entries allowed per upload`);
    }

    return entries;
  };

  const handleCsvUpload = (uploadedFile) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const csvText = e.target.result;
        const entries = parseCsvData(csvText);
        onUpload(uploadedFile, entries);
      } catch (error) {
        onError(error.message || "Error parsing CSV file");
      }
    };
    reader.onerror = () => {
      onError("Error reading CSV file");
    };
    reader.readAsText(uploadedFile);
  };

  const downloadSampleCSV = () => {
    const sampleData = [
      ["john.doe@example.com", "learn.johndoe.com"],
      ["jane.smith@gmail.com", "business.janesmith.co.uk"],
      ["mike.johnson@gmail.com", "academy.mikejohnson.org"],
    ];

    const csvColumnNames = "email,host";
    const csvContent = sampleData.map((row) => row.join(",")).join("\n");
    const csv = `${csvColumnNames}\n${csvContent}`;

    fileDownload(csv, "CSV Template for Managing Features.csv");
    message.success("Sample CSV downloaded successfully!");
  };

  return (
    <div className="upload-users-csv" style={{ padding: "20px 0" }}>
      <div style={{ marginBottom: 16 }}>
        <Title level={5} style={{ marginBottom: 8 }}>
          Upload CSV File
        </Title>
        <Text type="secondary">
          Upload a CSV file with creator entries. Required columns:{" "}
          <Text strong>email, host</Text>
        </Text>
      </div>

      <Button
        type="link"
        icon={<DownloadOutlined />}
        onClick={downloadSampleCSV}
        style={{ padding: 0, marginBottom: 16, color: COLORS.INFO }}
      >
        Download Sample CSV
      </Button>

      <div className="upload-dragger-container">
        <Upload.Dragger
          name="csvFile"
          accept=".csv"
          multiple={false}
          beforeUpload={(uploadFile) => {
            handleCsvUpload(uploadFile);
            return false;
          }}
          showUploadList={false}
          disabled={disabled}
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>

          <p className="ant-upload-text">
            Click or drag CSV file to this area to upload
          </p>

          <p className="ant-upload-hint">
            Only CSV files are supported. Maximum {MAX_ENTRIES} entries per
            upload. The file will be validated before updates are applied.
          </p>
        </Upload.Dragger>
      </div>

      <Alert
        message="Important Notes"
        description={
          <div>
            <ul style={{ margin: 0, paddingLeft: 20, marginBottom: 12 }}>
              <li>
                <Text strong>email:</Text> Creator&apos;s email address.
                Required field.
              </li>
              <li>
                <Text strong>host:</Text> Creator&apos;s host domain (e.g.,
                learn.johndoe.com). Required field.
              </li>
              <li>
                <Text strong>Validation:</Text> After upload, each entry will be
                validated against the server. Valid entries will be enriched
                with the creator&apos;s current feature configuration, tier, and
                plan type.
              </li>
              <li>
                <Text strong>Errors:</Text> If any entries fail validation, you
                can download an error CSV, fix the issues, and re-upload.
              </li>
              <li>
                <Text strong>Limit:</Text> Maximum {MAX_ENTRIES} entries per
                upload. Duplicate email + host combinations are automatically
                deduplicated.
              </li>
            </ul>
            <div
              style={{
                background: "#f0f5ff",
                padding: "12px",
                borderRadius: "4px",
                marginTop: 12,
              }}
            >
              <Text strong style={{ display: "block", marginBottom: 8 }}>
                How it works:
              </Text>
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                <li>
                  Upload your CSV with <Text code>email</Text> and{" "}
                  <Text code>host</Text> columns
                </li>
                <li>
                  The system validates each entry and fetches their current
                  feature configuration
                </li>
                <li>
                  Review and modify the feature flags as needed in the table
                </li>
                <li>Click Save to apply the changes</li>
              </ul>
            </div>
          </div>
        }
        type="info"
        showIcon
        style={{ marginTop: 16 }}
      />
    </div>
  );
};

UploadUsersCSV.propTypes = {
  onUpload: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

export default UploadUsersCSV;
